import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";
import { Loader2, CheckCircle, Clock, XCircle, Save } from "lucide-react";

const statusOptions = [
  { value: "present", label: "Present", cls: "bg-green-600", icon: CheckCircle },
  { value: "late", label: "Late", cls: "bg-yellow-600", icon: Clock },
  { value: "absent", label: "Absent", cls: "bg-red-600", icon: XCircle },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

function AttendanceManagementPage() {
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [date, setDate] = useState(todayStr());
  const [roster, setRoster] = useState([]);
  const [marks, setMarks] = useState({}); // studentId -> status
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const loadBatches = async () => {
      try {
        const res = await axiosInstance.get(apiUrl.GET_BATCHES);
        const list = res.data?.data || res.data || [];
        setBatches(list);
        if (list.length) setSelectedBatch(list[0]._id);
      } catch (err) {
        setMessage({ type: "error", text: "Failed to load batches" });
      } finally {
        setLoadingBatches(false);
      }
    };
    loadBatches();
  }, []);

  const loadRoster = useCallback(async () => {
    if (!selectedBatch) return;
    setLoadingRoster(true);
    setMessage(null);
    try {
      const res = await axiosInstance.get(apiUrl.ATTENDANCE_ROSTER(selectedBatch), {
        params: { date },
      });
      const students = res.data?.data?.roster || [];
      setRoster(students);
      const prefill = {};
      students.forEach((s) => {
        if (s.status) prefill[s.studentId] = s.status;
      });
      setMarks(prefill);
    } catch (err) {
      setMessage({ type: "error", text: "Failed to load student roster for this batch" });
      setRoster([]);
    } finally {
      setLoadingRoster(false);
    }
  }, [selectedBatch, date]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const setMark = (studentId, status) => {
    setMarks((prev) => ({ ...prev, [studentId]: status }));
  };

  const markAll = (status) => {
    const next = {};
    roster.forEach((s) => (next[s.studentId] = status));
    setMarks(next);
  };

  const handleSave = async () => {
    const records = Object.entries(marks).map(([studentId, status]) => ({
      studentId,
      status,
    }));
    if (!records.length) {
      setMessage({ type: "error", text: "Mark at least one student before saving" });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await axiosInstance.post(apiUrl.ATTENDANCE_MARK(selectedBatch), {
        date,
        records,
      });
      setMessage({ type: "success", text: "Attendance saved successfully" });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to save attendance",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Attendance Management</h1>
        <p className="text-slate-400 text-sm">
          Mark daily attendance batch-wise. Students see this live in their panel.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4 bg-[#121826] border border-[#223050] rounded-xl p-4">
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Batch</label>
          {loadingBatches ? (
            <Loader2 className="animate-spin text-slate-400" size={18} />
          ) : (
            <select
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              className="rounded-lg border border-[#1d3350] bg-[#0d1220] text-slate-200 px-3 py-2 text-sm min-w-[260px]"
            >
              {batches.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.batchName} — {b.courseName} ({b.batchId})
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Date</label>
          <input
            type="date"
            value={date}
            max={todayStr()}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-lg border border-[#1d3350] bg-[#0d1220] text-slate-200 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex gap-2">
          {statusOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => markAll(opt.value)}
              className={`text-xs px-3 py-2 rounded-lg text-white ${opt.cls} opacity-80 hover:opacity-100`}
            >
              Mark all {opt.label}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div
          className={`px-4 py-2 rounded-lg text-sm ${
            message.type === "success"
              ? "bg-green-500/10 text-green-400"
              : "bg-red-500/10 text-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="bg-[#121826] border border-[#223050] rounded-xl overflow-hidden">
        {loadingRoster ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        ) : roster.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            No active students enrolled in this batch.
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="text-slate-400 text-xs border-b border-[#223050]">
                <th className="p-3">Student</th>
                <th className="p-3">Course</th>
                <th className="p-3">Mark</th>
              </tr>
            </thead>
            <tbody>
              {roster.map((s) => (
                <tr key={s.studentId} className="border-b border-[#223050]/50">
                  <td className="p-3 text-slate-200">
                    {s.name}{" "}
                    <span className="text-slate-500 text-xs">({s.studentCode})</span>
                  </td>
                  <td className="p-3 text-slate-400 text-sm capitalize">{s.courseName}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      {statusOptions.map((opt) => {
                        const Icon = opt.icon;
                        const active = marks[s.studentId] === opt.value;
                        return (
                          <button
                            key={opt.value}
                            onClick={() => setMark(s.studentId, opt.value)}
                            className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                              active
                                ? `${opt.cls} text-white border-transparent`
                                : "bg-transparent text-slate-400 border-[#223050] hover:border-slate-500"
                            }`}
                          >
                            <Icon size={13} /> {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {roster.length > 0 && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-6 py-3 rounded-lg font-semibold"
        >
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          Save Attendance
        </button>
      )}
    </div>
  );
}

export default AttendanceManagementPage;
